import { useState, useCallback } from "react";

const SAVE_FEEDBACK_INTERVAL = 3;
const STORAGE_KEY_SAVE_COUNT = "autumn_save_count";
const STORAGE_KEY_SAVE_FEEDBACK_DISMISSED = "autumn_save_feedback_dismissed";

function getSaveCount(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SAVE_COUNT);
    if (raw === null) return 0;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

function getDismissedThreshold(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SAVE_FEEDBACK_DISMISSED);
    if (raw === null) return null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

function getCurrentThreshold(count: number): number | null {
  if (count > 0 && count % SAVE_FEEDBACK_INTERVAL === 0) return count;
  return null;
}

function computeShouldShow(count: number): boolean {
  const threshold = getCurrentThreshold(count);
  if (threshold === null) return false;
  const dismissed = getDismissedThreshold();
  return dismissed !== threshold;
}

export function useSaveCounter() {
  const initialCount = getSaveCount();
  const [count, setCount] = useState(initialCount);
  const [shouldShowFeedback, setShouldShowFeedback] = useState(() =>
    computeShouldShow(initialCount)
  );

  const incrementSaveCount = useCallback(() => {
    const current = getSaveCount();
    const next = current + 1;
    try {
      localStorage.setItem(STORAGE_KEY_SAVE_COUNT, String(next));
    } catch {
      return;
    }
    setCount(next);
    setShouldShowFeedback(computeShouldShow(next));
  }, []);

  const dismissFeedback = useCallback(() => {
    const threshold = getCurrentThreshold(count);
    if (threshold !== null) {
      try {
        localStorage.setItem(STORAGE_KEY_SAVE_FEEDBACK_DISMISSED, String(threshold));
      } catch {
        // localStorage full — still update UI state
      }
    }
    setShouldShowFeedback(false);
  }, [count]);

  return { shouldShowFeedback, dismissFeedback, incrementSaveCount };
}
